'use client'

import React from 'react'
import type { GridBlock as GridBlockProps } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import { getSectionHeadingLevels } from '@/blocks/SectionHeader/getSectionHeadingLevels'

type Props = GridBlockProps & {
  disableInnerContainer?: boolean
  hasHero?: boolean
  isFirstSectionHeader?: boolean
}

export const GridBlock: React.FC<Props> = ({
  sectionTag,
  heading,
  items,
  paddingTop,
  paddingBottom,
  hasHero,
  isFirstSectionHeader,
}) => {
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })
  const gridItems = (items || []).slice(0, 4)
  const { headingLevel: HeadingTag, eyebrowLevel: EyebrowTag } = getSectionHeadingLevels({ hasHero, isFirstSectionHeader, hasHeading: Boolean(heading) })

  return (
    <div className={cn('flex flex-col gap-10 lg:gap-16 w-full', paddingClasses)}>
      {/* Section Header */}
      {sectionTag && (
        <div className="flex flex-col gap-2 lg:gap-4">
          <EyebrowTag className="subheading-xsmall text-black mb-0">{sectionTag}</EyebrowTag>
          <div className="h-px w-full bg-civo-cloud" />
        </div>
      )}

      {/* Main Content Container */}
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
        {/* Heading */}
        <div className="lg:w-1/3">
          <HeadingTag className="display-large text-black">{heading}</HeadingTag>
        </div>

        {/* Grid Items */}
        <div className="lg:w-2/3 grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4 lg:gap-y-12">
          {gridItems.map((item, index) => (
            <div
              key={index}
              className={cn(
                'flex flex-col gap-4 pb-4 lg:pb-12 border-b border-civo-cloud',
                index >= 2 && 'lg:border-b-0 lg:pb-0'
              )}
            >
              {/* Item number - desktop only */}
              <p className="hidden lg:block subheading-xsmall text-black">
                {String(index + 1).padStart(2, '0')}
              </p>
              <h3 className="subheading-large text-black">{item.title}</h3>
              <p className="body-large text-black/70">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}